import React from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Plus, Search } from "lucide-react";
import type { ConsultPatient } from "../hooks/useConsultSearch";

type QuickRecordPatientSearchProps = {
  value: string;
  loading: boolean;
  results: ConsultPatient[];
  selectedPatientId: string | null;
  onChangeValue: (value: string) => void;
  onSelectPatient: (patient: ConsultPatient) => void;
  onCreateNewPatient: () => void;
};

export const QuickRecordPatientSearch: React.FC<
  QuickRecordPatientSearchProps
> = ({
  value,
  loading,
  results,
  selectedPatientId,
  onChangeValue,
  onSelectPatient,
  onCreateNewPatient,
}) => {
  const showNoResults =
    value.trim().length > 1 && !loading && results.length === 0;

  return (
    <View style={styles.container}>
      <Text style={styles.question}>
        Which patient do you want to add this recording to?
      </Text>

      <View style={styles.searchWrapper}>
        <Search size={18} color="#77818f" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search patients by name, email, or phone"
          placeholderTextColor="#8e96a3"
          value={value}
          onChangeText={onChangeValue}
        />
        {loading && <ActivityIndicator size="small" color="#0085FF" />}
      </View>

      <TouchableOpacity style={styles.addButton} onPress={onCreateNewPatient}>
        <Plus size={16} color="#0066cc" />
        <Text style={styles.addButtonText}>Add a patient</Text>
      </TouchableOpacity>

      <ScrollView
        style={styles.resultsContainer}
        contentContainerStyle={styles.resultsContent}
      >
        {results.map((patient) => {
          const patientId = String(
            patient.patientRecordId ||
              patient.clinicOSUUID ||
              patient.id ||
              patient.name,
          );
          const isSelected = selectedPatientId === patientId;

          return (
            <TouchableOpacity
              key={patientId}
              style={[
                styles.patientRow,
                isSelected && styles.patientRowSelected,
              ]}
              onPress={() => onSelectPatient(patient)}
            >
              <Text style={styles.patientName}>{patient.name}</Text>
              <Text style={styles.patientMeta}>
                {patient.email || patient.phone || "No contact info"}
              </Text>
            </TouchableOpacity>
          );
        })}

        {showNoResults && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No patients found.</Text>
            <Text style={styles.emptySubtext}>
              Try a different search or add a new patient.
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  question: {
    fontSize: 18,
    fontWeight: "600",
    color: "#1e2733",
  },
  searchWrapper: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
    borderColor: "#d5dce5",
    borderRadius: 10,
    backgroundColor: "#ffffff",
    paddingHorizontal: 12,
    height: 44,
  },
  searchInput: {
    flex: 1,
    color: "#1e2733",
    fontSize: 14,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#eaf4ff",
  },
  addButtonText: {
    color: "#0066cc",
    fontSize: 13,
    fontWeight: "600",
  },
  resultsContainer: {
    maxHeight: 280,
    minHeight: 120,
    borderWidth: 1,
    borderColor: "#e4e9ef",
    borderRadius: 10,
    backgroundColor: "#ffffff",
  },
  resultsContent: {
    padding: 8,
    gap: 8,
  },
  patientRow: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e9ef",
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  patientRowSelected: {
    borderColor: "#0085FF",
    backgroundColor: "#eef7ff",
  },
  patientName: {
    color: "#1f2937",
    fontSize: 14,
    fontWeight: "600",
  },
  patientMeta: {
    color: "#6b7280",
    fontSize: 12,
    marginTop: 4,
  },
  emptyState: {
    paddingVertical: 16,
    alignItems: "center",
  },
  emptyText: {
    color: "#374151",
    fontWeight: "600",
  },
  emptySubtext: {
    color: "#6b7280",
    marginTop: 4,
  },
});
